"use client";

import { useLazyQuery } from "@apollo/client/react";
import type {
  CellContext,
  Column,
  ColumnDef,
  PaginationState,
  Row,
  SortingState,
} from "@tanstack/react-table";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { LuBadgeDollarSign, LuFlame, LuLock } from "react-icons/lu";

import { getBaseCurrencyPrice } from "@/backend/deso";
import TradeCoinActions from "@/components/app/trade/actions/trade-coin-actions";
import {
  renderApyTooltip,
  renderCirculatingSupplyTableCell,
  renderCirculatingSupplyTooltip,
  renderCoinUserTableCell,
  renderMarketCapTableCell,
  renderMarketCapTooltip,
  renderPriceUsdTableCell,
  renderVolumeTableCell,
} from "@/components/app/trade/trending-coins/trending-coins-cell-content";
import TrendingCoinsTable from "@/components/app/trade/trending-coins/trending-coins-table";
import CoinPriceChangePercentageFormatter from "@/components/shared/coin-price-change-percentage-formatter";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import TablePagination from "@/components/shared/table-pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import {
  type UserDaoCoinStat,
  UserDaoCoinStatsDocument,
  UserDaoCoinStatsOrderBy,
} from "@/graphql/codegen/graphql";
import {
  DESO_APP_NAME,
  DESO_TOKEN_DISPLAY_NAME,
  MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING,
} from "@/utils/constants";
import {
  basisPointsToPercent,
  nanosToInteger,
  tokenNanosToInteger,
} from "@/utils/currency";
import { abbreviateNumber, pluralize } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { getOrderByKey } from "@/utils/trade";

import { renderSortingHeader } from "../trade/table-sorting-headers";
import { WalletLockedTokensCell } from "./wallet-locked-tokens-cell";

const isAbortError = (error: unknown) =>
  error instanceof Error && error.name === "AbortError";

interface WalletCreatorCoinHoldingsProps {
  publicKey: string;
  isMyWallet: boolean;
  hasCoin: boolean;
}

const COLUMNS_WIDTH = [3, 14, 9, 9, 9, 8, 7, 10, 10, 11, 6, 4];
const DEFAULT_ITEMS_PER_PAGE = 10;

interface CustomCellContext extends CellContext<UserDaoCoinStat, unknown> {
  focusExchangeRate: number;
  desoExchangeRate: number;
  pageOffset: number;
  currentUserPublicKey: string;
  refetchData: () => void;
}

enum TabsValue {
  MyTokens = "myTokens",
  HiddenTokens = "hiddenTokens",
}

const WalletCreatorCoinHoldings = ({
  publicKey,
  isMyWallet,
  hasCoin,
}: WalletCreatorCoinHoldingsProps) => {
  const holdingsRequestIdRef = useRef(0);
  const myTokenRequestIdRef = useRef(0);
  const [loadingMyToken, setLoadingMyToken] = useState(true);
  const [myToken, setMyToken] = useState<UserDaoCoinStat | null>(null);
  const [firstLoading, setFirstLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userDesoTokenHoldings, setUserDesoTokenHoldings] = useState<
    Partial<UserDaoCoinStat>[]
  >([]);
  const [pageOffset, setPageOffset] = useState(0);
  const [itemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [columnSorting, setColumnSorting] = useState<SortingState>([
    { id: "priceUsdPctChange24H", desc: true },
  ]);
  const [currentTab, setCurrentTab] = useState<string>(TabsValue.MyTokens);

  const TabItems = useMemo(
    () => [
      {
        value: TabsValue.MyTokens,
        label: `${isMyWallet ? "My" : "Their"} Tokens`,
        tooltip: `Tokens that ${isMyWallet ? "you" : "they've"} buy or explicitly whitelist 
            will appear in this tab. This prevents people from polluting ${isMyWallet ? "your" : "their"} 
            wallet with spam tokens that ${isMyWallet ? "you've" : "they've"} never bought or heard of. 
            ${isMyWallet ? "You" : "Token owners"} can always move a token back and forth 
            from the spam folder by using the option in three dots menu on the side`,
      },
      {
        value: TabsValue.HiddenTokens,
        label: "Hidden Tokens",
        tooltip: `When someone sends you a token that you've never bought 
        or explicitly whitelisted, it will appear in the "Hidden Tokens" 
        tab. This prevents people from polluting your wallet with spam 
        tokens that you've never bought or heard of. You can always move 
        a token back and forth from the Hidden tab by using the option 
        in three dots menu on the side.`,
      },
    ],
    [isMyWallet],
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: itemsPerPage,
  });

  const [fetchMyTokenDaoCoinStats] = useLazyQuery(UserDaoCoinStatsDocument, {
    fetchPolicy: "network-only",
  });
  const [fetchHoldingsDaoCoinStats] = useLazyQuery(UserDaoCoinStatsDocument, {
    fetchPolicy: "network-only",
  });

  const fetchMyToken = async () => {
    if (!hasCoin) {
      setMyToken(null);
      setLoadingMyToken(false);
      return;
    }

    const requestId = ++myTokenRequestIdRef.current;
    setLoadingMyToken(true);

    try {
      const request = fetchMyTokenDaoCoinStats({
        variables: {
          filter: {
            userPublicKey: {
              equalTo: publicKey,
            },
            coinUsername: {
              notEqualTo: "",
            },
            coinPublicKey: {
              equalTo: publicKey,
            },
          },
        },
      });
      request.retain?.();

      const { data } = await request;

      if (requestId !== myTokenRequestIdRef.current) {
        return;
      }

      setMyToken(data?.userDaoCoinStats?.nodes.filter(Boolean)?.[0] || null);
    } catch (e) {
      if (isAbortError(e) || requestId !== myTokenRequestIdRef.current) {
        return;
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when fetching your ${DESO_TOKEN_DISPLAY_NAME} token`,
        details: createToastErrorDetails(e),
      });
    } finally {
      if (requestId === myTokenRequestIdRef.current) {
        setLoadingMyToken(false);
      }
    }
  };

  const refetchWithUpdatedParams = useCallback(
    async (
      params: {
        first?: number;
        offset?: number;
      } = { first: itemsPerPage, offset: pageOffset },
    ) => {
      const requestId = ++holdingsRequestIdRef.current;
      setLoading(true);

      try {
        const request = fetchHoldingsDaoCoinStats({
          variables: {
            orderBy: getOrderByKey(
              columnSorting,
              UserDaoCoinStatsOrderBy,
              UserDaoCoinStatsOrderBy.PriceUsdPctChange_24HDesc,
            ),
            filter: {
              userPublicKey: {
                equalTo: publicKey,
              },
              coinUsername: {
                notEqualTo: "",
              },
              coinPublicKey: {
                notIn: [
                  process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
                  process.env.NEXT_PUBLIC_DUSD_PUBLIC_KEY,
                  process.env.NEXT_PUBLIC_DESO_BTC_PUBLIC_KEY,
                  process.env.NEXT_PUBLIC_DESO_ETH_PUBLIC_KEY,
                  process.env.NEXT_PUBLIC_DESO_SOL_PUBLIC_KEY,
                  publicKey,
                ],
              },
              isUserWhitelisted: {
                equalTo: currentTab === TabsValue.MyTokens,
              },
            },
            ...params,
          },
        });
        request.retain?.();

        const desoTokenStatsResponse = await request;

        const desoTokenStats =
          desoTokenStatsResponse.data?.userDaoCoinStats?.nodes.filter(
            Boolean,
          ) || [];

        if (requestId !== holdingsRequestIdRef.current) {
          return;
        }

        const prices = await getBaseCurrencyPrice(
          desoTokenStats.map((coin) => ({
            BaseCurrencyPublicKeyBase58Check: coin.coinPublicKey ?? "",
            BaseCurrencyQuantityToSell: tokenNanosToInteger(
              Number(coin.userBalanceNanos ?? "0"),
            ),
          })),
        );

        if (requestId !== holdingsRequestIdRef.current) {
          return;
        }

        const parsedDesoTokenStats = desoTokenStats.map((coin, index) => ({
          ...coin,
          userBalanceUsd: Number(prices[index].ReceiveAmountInUsd),
        }));

        setUserDesoTokenHoldings(parsedDesoTokenStats);
        setTotalCount(
          desoTokenStatsResponse.data?.userDaoCoinStats?.totalCount ?? 0,
        );
      } catch (e) {
        if (isAbortError(e) || requestId !== holdingsRequestIdRef.current) {
          return;
        }

        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened when fetching your ${DESO_TOKEN_DISPLAY_NAME} tokens`,
          details: createToastErrorDetails(e),
        });
      } finally {
        if (requestId === holdingsRequestIdRef.current) {
          setFirstLoading(false);
          setLoading(false);
        }
      }
    },
    [
      columnSorting,
      fetchHoldingsDaoCoinStats,
      itemsPerPage,
      pageOffset,
      publicKey,
      currentTab,
    ],
  );
  const onInitialLoad = useEffectEvent(() => {
    setFirstLoading(true);
    setTotalCount(0);
    setPageOffset(0);
    setPagination({
      pageIndex: 0,
      pageSize: DEFAULT_ITEMS_PER_PAGE,
    });
    setColumnSorting([{ id: "priceUsdPctChange24H", desc: true }]);
    void refetchWithUpdatedParams({
      first: DEFAULT_ITEMS_PER_PAGE,
      offset: 0,
    });
    void fetchMyToken();
  });
  const onItemsPerPageChange = useEffectEvent(() => {
    if (firstLoading || loading) {
      return;
    }

    setPageOffset(0);
    void refetchWithUpdatedParams({ first: itemsPerPage, offset: 0 });
  });
  const onSortOrTabChange = useEffectEvent(() => {
    if (firstLoading || loading) {
      return;
    }

    void refetchWithUpdatedParams();
  });

  const onPaginate = async (nextPageIndex: number) => {
    const offset = nextPageIndex * itemsPerPage;

    setPagination({
      pageIndex: nextPageIndex,
      pageSize: itemsPerPage,
    });

    await refetchWithUpdatedParams({
      first: itemsPerPage,
      offset,
    });

    setPageOffset(offset);
  };

  const getVisibleColumns = ({ sortable }: { sortable: boolean }) => [
    {
      accessorKey: "id",
      header: () => <span className="text-xxs text-muted">#</span>,
      cell: ({ row, pageOffset }: CustomCellContext) => {
        const { id } = row;
        return (
          <span className="text-xs text-muted">{+id + pageOffset + 1}</span>
        );
      },
    },
    {
      accessorKey: "coinPublicKey",
      header: <span className="text-xxs">Coin</span>,
      cell: ({ row, currentUserPublicKey }: CustomCellContext) => {
        const { coinUsername, coinPublicKey, coinCategory, coinExtraData } =
          row.original;

        return renderCoinUserTableCell({
          coinUsername: coinUsername ?? "",
          coinPublicKey: coinPublicKey ?? "",
          coinCategory: coinCategory ?? "",
          extraData: coinExtraData,
          clickable: true,
          isTokenCreator: coinPublicKey === currentUserPublicKey,
        });
      },
    },
    {
      accessorKey: "priceUsd",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Price",
          sortable,
          className: "text-xxs",
        }),
      cell: (props: CustomCellContext) => {
        const { row, focusExchangeRate, desoExchangeRate } = props;
        return renderPriceUsdTableCell(
          row.getValue("coinPublicKey"),
          row.getValue("priceUsd"),
          focusExchangeRate,
          desoExchangeRate,
        );
      },
    },
    {
      accessorKey: "userBalanceUsd",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Value",
          sortable,
          infoTooltip: (
            <InfoTooltip
              iconSize={16}
              text="This column shows the USD amount you would get if you market
          sold all of your holdings (including locked holdings). If some
          of your holdings are locked, the unlocked cashout value will be
          shown underneath the total value. If you have locked coins, you
          can see the full unlock schedule by clicking on the lock icon in
          the second row."
            />
          ),
          className: "text-xxs",
        }),
      cell: ({ row }: CustomCellContext) => {
        const totalUsdValue = row.original.userBalanceUsd ?? 0;
        const totalLockedUsdValue = row.original.userLockedBalanceUsd;

        return (
          <div className="flex flex-col gap-1 text-left text-xs">
            <CoinPriceFormatter
              price={totalUsdValue}
              highPriceThreshold={10}
              className="font-mono font-semibold text-muted-foreground"
            />
            <WalletLockedTokensCell
              refetchData={refetchWithUpdatedParams}
              totalLockedUsdValue={totalLockedUsdValue}
              coinPublicKey={row.original.coinPublicKey}
              coinUsername={row.original.coinUsername}
              coinPrice={row.original.priceUsd}
              holderPublicKey={publicKey}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "userBalanceUsdChange24H",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Value (24h)",
          sortable,
          infoTooltip: (
            <InfoTooltip
              iconSize={16}
              className="max-w-[400px]"
              text={
                <>
                  This column is an ESTIMATE of the change in the value of your
                  holdings in the last 24h. It is computed by first computing
                  the percent change in price in the last 24h, and then using
                  the following formula:
                  <br />
                  <br />
                  <span
                    className="
                      bg-accent px-2 py-3 font-mono text-xxs font-semibold
                    "
                  >
                    valueChange = valueToday * (1 - 1 / (1 + percentChange))
                  </span>
                  <br />
                  <br />
                  This is an estimate because the percent change in price is not
                  always equal to the percent change in cashout value due to
                  slippage. The more liquid the market, however, the more
                  accurate this estimate is.
                </>
              }
            />
          ),
          className: "text-xxs",
        }),
      cell: ({ row }: CustomCellContext) => {
        const totalUsdValueChange24h =
          row.original.userBalanceUsdChange24H ?? 0;
        const totalUsdPricePctChange24h =
          row.original.priceUsdPctChange24H ?? 0;

        const priceChanged =
          totalUsdPricePctChange24h < 0 ||
          totalUsdPricePctChange24h >=
            MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING;
        const isPositiveChange =
          totalUsdPricePctChange24h >=
          MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING;
        const wrapperClassName = priceChanged
          ? isPositiveChange
            ? "text-emerald-400"
            : "text-red-600"
          : "text-muted";

        return (
          <div className={cn("flex flex-col gap-1", wrapperClassName)}>
            <div className="flex items-center">
              {isPositiveChange ? "+" : ""}
              <CoinPriceFormatter
                price={totalUsdValueChange24h}
                highPriceThreshold={10}
                className="font-mono font-semibold"
              />
            </div>
            <div className="flex flex-row items-center gap-1 text-left text-xs">
              <CoinPriceChangePercentageFormatter
                price={row.original.priceUsdPctChange24H ?? 0}
                showArrow={true}
                arrowSize={12}
                className="inline-flex font-mono"
              />
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "userBalanceNanos",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Balance",
          sortable,
          className: "text-xxs",
        }),
      cell: ({ row }: { row: Row<Partial<UserDaoCoinStat>> }) => {
        const totalBalanceValueNanos = Number(
          row.original.userBalanceNanos ?? "0",
        );
        const totalLockedBalanceValueNanos = Number(
          row.original.userLockedBalanceNanos ?? "0",
        );

        return (
          <div className="flex flex-col gap-1 text-left text-xs">
            <CoinPriceFormatter
              price={nanosToInteger(totalBalanceValueNanos / 1e9)}
              isUsd={false}
              highPriceThreshold={10}
              className="font-mono font-semibold text-foreground"
            />
            {totalLockedBalanceValueNanos > 0 && (
              <div className="flex items-center gap-1 text-muted">
                <LuLock />
                <CoinPriceFormatter
                  price={nanosToInteger(totalLockedBalanceValueNanos / 1e9)}
                  isUsd={false}
                  className="font-mono text-muted"
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "apyBasisPoints",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "APY",
          sortable,
          infoTooltip: renderApyTooltip(),
          className: "text-xxs",
        }),
      cell: ({ row }: CustomCellContext) => (
        <div
          className="
            flex flex-col gap-0 text-left font-mono text-xs text-foreground
          "
        >
          <CoinPriceChangePercentageFormatter
            price={basisPointsToPercent(Number(row.original.apyBasisPoints))}
            showArrow={false}
            className="text-foreground"
          />
        </div>
      ),
    },
    {
      accessorKey: "volumeUsdPast24H",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Volume (24h)",
          sortable,
          className: "text-xxs",
        }),
      cell: (props: CustomCellContext) => {
        const { row, focusExchangeRate, desoExchangeRate } = props;

        return renderVolumeTableCell({
          publicKey: row.original.coinPublicKey ?? "",
          volumeUsd: row.original.volumeUsdPast24H ?? 0,
          focusExchangeRate,
          desoExchangeRate,
        });
      },
    },
    {
      accessorKey: "marketCapUsd",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Market Cap",
          sortable,
          infoTooltip: renderMarketCapTooltip(),
          className: "text-xxs",
        }),
      cell: (props: CustomCellContext) => {
        const { row, focusExchangeRate, desoExchangeRate } = props;

        return renderMarketCapTableCell({
          publicKey: row.original.coinPublicKey ?? "",
          marketCapUsd: row.original.marketCapUsd ?? 0,
          focusExchangeRate,
          desoExchangeRate,
        });
      },
    },
    {
      accessorKey: "circulatingSupply",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Circ. Supply",
          sortable,
          infoTooltip: renderCirculatingSupplyTooltip(),
          className: "text-xxs",
        }),
      cell: ({ row }: CustomCellContext) => {
        return renderCirculatingSupplyTableCell({
          circulatingSupply: Number(row.original.circulatingSupply ?? "0"),
          totalSupply: Number(row.original.totalSupply ?? "0"),
        });
      },
    },
    {
      accessorKey: "numHolders",
      header: ({ column }: { column: Column<UserDaoCoinStat, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Holders",
          sortable,
          className: "text-xxs",
        }),
      cell: ({ row }: CustomCellContext) => {
        return (
          <div className="flex w-full flex-col gap-0 text-xs">
            <div className="pr-3 text-right">
              {abbreviateNumber(row.getValue("numHolders"))}
            </div>
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row, refetchData }: CustomCellContext) => {
        return (
          <TradeCoinActions
            coinUsername={row.original.coinUsername ?? ""}
            coinPublicKey={row.original.coinPublicKey ?? ""}
            hasAmm={row.original.tokenAmmCreatedAt !== null}
            isMyWallet={isMyWallet}
            isWhitelist={currentTab === TabsValue.MyTokens}
            setLoading={setLoading}
            refetchData={refetchData}
            onSuccess={refetchWithUpdatedParams}
          />
        );
      },
    },
  ];

  useEffect(() => {
    onInitialLoad();
  }, [publicKey, hasCoin]);

  useEffect(() => {
    onItemsPerPageChange();
  }, [itemsPerPage]);

  useEffect(() => {
    onSortOrTabChange();
  }, [columnSorting, currentTab]);

  return (
    <div className="mb-4 w-full text-muted">
      {hasCoin && (
        <section>
          <div className="mb-4 w-full">
            <h3 className="font-shadow-green flex items-center text-emerald-400">
              <LuFlame className="mr-2 text-xl" />
              {isMyWallet ? "Your" : "Their"} Token
            </h3>
          </div>

          {loadingMyToken ? (
            <Skeleton className="mt-4 h-[120px] w-full" key="tokens-key" />
          ) : (
            <TrendingCoinsTable
              wrapperClassName="min-w-[1350px]"
              loading={loadingMyToken}
              columns={
                getVisibleColumns({ sortable: false }) as Array<
                  ColumnDef<UserDaoCoinStat>
                >
              }
              data={myToken ? [myToken] : []}
              columnSorting={[{ id: "priceUsdPctChange24H", desc: true }]}
              setColumnSorting={() => {}}
              columnsWidth={COLUMNS_WIDTH}
              pagination={{
                pageIndex: 0,
                pageSize: 1,
              }}
              pageOffset={0}
              refetchData={() => {}}
              emptyTableClassName="max-w-[1000px] sticky flex items-center justify-center top-0 left-0"
            />
          )}
        </section>
      )}

      <section>
        <div className="mt-12 mb-4 w-full">
          <h3 className="flex items-center">
            <LuBadgeDollarSign className="mr-2 text-xl" />
            {DESO_APP_NAME} Tokens
          </h3>
        </div>

        {firstLoading ? (
          <Skeleton className="mt-4 h-[320px] w-full" key="tokens-key" />
        ) : (
          <Tabs
            defaultValue={currentTab}
            onValueChange={(val) => setCurrentTab(val)}
          >
            <TabsList className="mb-3 border-none">
              {TabItems.map((tab) => (
                <TabsTrigger value={tab.value} key={tab.value}>
                  <InfoTooltip text={tab.tooltip}>
                    <span>{tab.label}</span>
                  </InfoTooltip>
                </TabsTrigger>
              ))}
            </TabsList>
            {TabItems.map((tab) => (
              <TabsContent value={tab.value} key={tab.value}>
                <TrendingCoinsTable
                  wrapperClassName="min-w-[1350px]"
                  loading={loading}
                  columns={
                    getVisibleColumns({ sortable: true }) as Array<
                      ColumnDef<UserDaoCoinStat>
                    >
                  }
                  data={userDesoTokenHoldings}
                  columnSorting={columnSorting}
                  setColumnSorting={setColumnSorting}
                  pagination={pagination}
                  pageOffset={pageOffset}
                  columnsWidth={COLUMNS_WIDTH}
                  refetchData={refetchWithUpdatedParams}
                  emptyTableClassName="max-w-[1000px] sticky flex items-center justify-center top-0 left-0"
                />
                <TablePagination
                  loading={loading}
                  pagination={pagination}
                  onPaginate={onPaginate}
                  itemsPerPage={itemsPerPage}
                  totalCount={totalCount}
                />

                <div className="mt-2 mb-4 text-center text-xs text-muted">
                  Total:{" "}
                  {pluralize(totalCount, `${DESO_TOKEN_DISPLAY_NAME} Token`)}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </section>
    </div>
  );
};

export default WalletCreatorCoinHoldings;
